import { select, call, take, all, getContext } from "redux-saga/effects";
import { selectUser, selectUserId } from "reducers/user";
import { REPLACE_ACCOUNT_META } from "constants/account/meta";
import {
  REPLACE_ACCOUNT_USERS,
  UPDATE_ACCOUNT_FEATURES,
  REPLACE_ACCOUNT_SEGMENTS,
  REPLACE_ACCOUNT_CHECKLISTS
} from "constants/actionTypes";
import { resolve as resolveFlows } from "actions/account/flows";
import { USER_REPLACED } from "actions/user";
import { CHROME_EXTENSION_STATUS_UPDATED } from "constants/chromeExtension/actionTypes";
import { selectAccountMeta } from "reducers/account/meta";
import { selectAccountUsers } from "reducers/account/users";
import { selectFlows } from "reducers/account/flows";
import { selectAccountChecklists } from "reducers/account/checklists";
import { selectAccountSegments } from "reducers/account/segments";
import { selectAccountFeatures } from "reducers/account/features";
import {
  selectChromeExtensionVersion,
  selectHasChromeExtensionInstalled
} from "reducers/chromeExtension";

export default function* identifier() {
  // wait for all collections to resolve before checking isSpoofing, since
  // isSpoofing returns a Promise that fails to resolve if called too early

  yield all([
    take(REPLACE_ACCOUNT_META),
    take(REPLACE_ACCOUNT_USERS),
    take(UPDATE_ACCOUNT_FEATURES),
    take(USER_REPLACED),
    take(REPLACE_ACCOUNT_CHECKLISTS),
    take(REPLACE_ACCOUNT_SEGMENTS),
    take(resolveFlows),
    take(CHROME_EXTENSION_STATUS_UPDATED)
  ]);

  const auth = yield getContext("auth");
  const isSpoofing = yield call(auth.isSpoofing);

  if (!isSpoofing) {
    const {
      id: account_id,
      buffer: account_buffer,
      company,
      createdAt: account_createdAt,
      flowsShown: account_flowsShown = 0,
      flowsShownLimit: account_flowsShownLimit = 50,
      isInstalled: account_isInstalled,
      isTrial: account_isTrial,
      isTrialExpired: account_isTrialExpired,
      lastSeenTimestamp: account_lastSeenTimestamp,
      npsScore: account_npsScore,
      stripeId: account_stripeId,
      stripePlanId: account_stripePlanId,
      stripeSubCreatedAt: account_stripeSubCreatedAt
    } = yield select(selectAccountMeta) || {};

    const users = yield select(selectAccountUsers) || {};

    const {
      email,
      firstUsedComponents,
      fullname,
      isInvitedUser,
      lastSeenTimestamp,
      role,
      createdAt
    } = yield select(selectUser) || {};

    const id = yield select(selectUserId);

    const flows = yield select(selectFlows) || {};
    const checklists = yield select(selectAccountChecklists) || {};
    const segments = yield select(selectAccountSegments) || {};

    const features = yield select(selectAccountFeatures) || {};
    const { HAS_BETA_FEATURES } = features;

    const hasCrxInstalled = yield select(selectHasChromeExtensionInstalled);
    const crxVersion = yield select(selectChromeExtensionVersion);

    // use a reference to window.analytics instead of defining it as an external
    // the analytics.js snippet defines "queueing stubs" and uses a
    // createElement to load itself asynchronously.

    // on load, the stub invocations are replayed, but since webpack has wrapped
    // the snippet in a non-window context, analytics.js can't access the
    // queues, and all invocations (read: identifies) are lost.

    if (window.analytics && window.analytics.identify) {
      const traits = {
        Account_created_at: account_createdAt,
        accountId: account_id,
        account_buffer,
        account_createdAt,
        ...Object.keys(features).reduce((acc, feature) => {
          acc[`account_feature_${feature}`] = features[feature];
          return acc;
        }, {}),
        account_flowsShown,
        account_flowsShownLimit,
        account_id,
        account_isBeta: HAS_BETA_FEATURES,
        account_isInstalled,
        account_isTrial,
        account_isTrialExpired,
        account_lastSeenTimestamp,
        account_npsScore,
        account_stripeId,
        account_stripePlanId,
        account_stripeSubCreatedAt,
        appcuesId: account_id,
        company,
        createdAt,
        created_at: createdAt,
        crxVersion,
        crxVersionFromClient: crxVersion,
        email,
        embedCodeInstalled: account_isInstalled,
        firstUsedComponents,
        fullname,
        hasCrxInstalled,
        id,
        isAdmin: role === "ACCOUNT_ADMIN",
        isBeta: HAS_BETA_FEATURES,
        isInstalled: account_isInstalled,
        isInvitedUser,
        isTrial: account_isTrial,
        isTrialExpired: account_isTrialExpired,
        is_trial: account_isTrial,
        lastSeenTimestamp,
        npsScore: account_npsScore,
        numFlows: Object.keys(flows).length,
        only_user_in_account: Object.keys(users) === 1,
        published_checklist_count: Object.values(checklists).filter(
          ({ published }) => published
        ).length,
        published_journey_count: Object.values(flows).filter(
          ({ published }) => published
        ).length,
        role,
        segments_count: Object.keys(segments).length,
        stripeId: account_stripeId,
        stripePlanId: account_stripePlanId,
        stripeSubCreatedAt: account_stripeSubCreatedAt,
        totalFlows: Object.keys(flows).length,
        totalFlowsPublished: Object.values(flows).filter(
          ({ published }) => published
        ).length,
        unpublished_journey_count: Object.values(flows).filter(
          ({ published }) => !published
        ).length,
        userEmail: email,
        userId: id,
        userRole: role,
        users_count: Object.keys(users).length
      };
      window.analytics.identify(id, traits);

      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/models/account.coffee#L97-L98
      window.analytics.group(account_id);
    }

    if (window.ga && window.ga.set) {
      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/models/account.coffee#L100-L101
      window.ga.set("&uid", account_id);
    }

    if (account_stripeId && window.profitwell) {
      /* global PROFITWELL_AUTH_TOKEN */

      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/assets/index.html#L448
      window.profitwell("auth_token", `${PROFITWELL_AUTH_TOKEN}`);

      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/models/account.coffee#L103-L106
      window.profitwell("user_stripe_id", account_stripeId);
    }
  }
}
